import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemButton
} from '@mui/material'
import SpaceTheme from '../components/SpaceTheme'
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db, storage } from '../firebase/config'
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import toast from 'react-hot-toast'

const SendDocument = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedStudents, setSelectedStudents] = useState([])
  const [teacherClasses, setTeacherClasses] = useState([])
  const [students, setStudents] = useState([])
  
  // Dialog state'leri
  const [showClassDialog, setShowClassDialog] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)
  const [showStudentDialog, setShowStudentDialog] = useState(false)
  const [classStudents, setClassStudents] = useState([])

  useEffect(() => {
    if (user) {
      loadTeacherClasses()
    }
  }, [user])

  const loadTeacherClasses = async () => {
    try {
      setLoading(true)
      const teacherId = user.uid
      console.log('Loading classes for teacher:', teacherId)
      
      // Öğretmenin sınıflarını yükle
      const classesRef = collection(db, 'classes')
      const q = query(classesRef, where('teacherId', '==', teacherId))
      const querySnapshot = await getDocs(q)
      
      console.log('Found classes:', querySnapshot.docs.length)
      
      const classesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      console.log('Classes data:', classesData)
      setTeacherClasses(classesData)
      
      // Her sınıf için öğrencileri yükle
      const allStudents = []
      for (const classData of classesData) {
        const studentsRef = collection(db, 'users')
        const studentsQuery = query(
          studentsRef, 
          where('role', '==', 'student'),
          where('classIds', 'array-contains', classData.id)
        )
        const studentsSnapshot = await getDocs(studentsQuery)
        
        const classStudents = studentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          className: classData.className,
          classId: classData.id
        }))
        allStudents.push(...classStudents)
      }
      setStudents(allStudents)
    } catch (error) {
      console.error('Error loading classes:', error)
      toast.error('Sınıflar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır')
        return
      }
      
      // Dosya tipi kontrolü
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/jpeg',
        'image/png',
        'image/gif',
        'text/plain'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Desteklenmeyen dosya tipi')
        return
      }
      
      setSelectedFile(file)
    }
  }

  const handleClassClick = (classData) => {
    setSelectedClass(classData)
    setShowClassDialog(true)
  }

  const handleSelectAllStudents = () => {
    if (!selectedClass) return
    
    const classStudentList = students.filter(s => s.classId === selectedClass.id)
    
    if (classStudentList.length === 0) {
      toast.error('Bu sınıfta öğrenci yok!')
      return
    }
    
    // Mevcut seçimlere ekle (duplicate kontrolü ile)
    setSelectedStudents(prev => {
      const newStudents = [...prev]
      classStudentList.forEach(student => {
        if (!newStudents.some(s => s.id === student.id)) {
          newStudents.push(student)
        }
      })
      return newStudents
    })
    
    toast.success(`Tüm sınıf seçildi (${classStudentList.length} öğrenci)`)
    setShowClassDialog(false)
  }

  const handleIndividualSelection = () => {
    if (!selectedClass) return
    
    const classStudentList = students.filter(s => s.classId === selectedClass.id)
    
    if (classStudentList.length === 0) {
      toast.error('Bu sınıfta öğrenci yok!')
      return
    }
    
    setClassStudents(classStudentList)
    setShowClassDialog(false)
    setShowStudentDialog(true)
  }

  const handleStudentToggle = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.some(s => s.id === student.id)
      if (isSelected) {
        return prev.filter(s => s.id !== student.id)
      } else {
        return [...prev, student]
      }
    })
  }

  const handleSendDocument = async () => {
    if (!selectedFile) {
      toast.error('Lütfen bir döküman seçin')
      return
    }

    if (selectedStudents.length === 0) {
      toast.error('Lütfen en az bir öğrenci seçin')
      return
    }

    setUploading(true)

    try {
      const teacherId = user.uid
      const fileName = `documents/${teacherId}/${Date.now()}_${selectedFile.name}`
      const storageRef = ref(storage, fileName)

      // Dosyayı yükle
      const snapshot = await uploadBytes(storageRef, selectedFile)
      const downloadURL = await getDownloadURL(snapshot.ref)

      // Seçilen öğrenciler için dökümanı kaydet
      const documentData = {
        teacherId,
        documentName: selectedFile.name,
        documentUrl: downloadURL,
        fileName,
        sentDate: new Date(),
        documentType: 'individual',
        studentIds: selectedStudents.map(s => s.id),
        studentNames: selectedStudents.map(s => s.username || s.email),
        // Sınıf bilgilerini de sakla
        sourceClassId: selectedStudents[0]?.classId,
        sourceClassName: selectedStudents[0]?.className
      }

      await addDoc(collection(db, 'sentDocuments'), documentData)

      toast.success('Döküman başarıyla gönderildi!')
      onBack() // Ana sayfaya dön
    } catch (error) {
      console.error('Error sending document:', error)
      toast.error('Döküman gönderilirken hata oluştu')
    } finally {
      setUploading(false)
    }
  }

  const clearSelectedFile = () => {
    setSelectedFile(null)
    // File input'u temizle
    const fileInput = document.getElementById('file-input')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const clearSelectedStudents = () => {
    setSelectedStudents([])
    toast.success('Seçilen öğrenciler temizlendi')
  }

  const getSelectedClasses = () => {
    const classIds = [...new Set(selectedStudents.map(s => s.classId))]
    return classIds.map(classId => {
      const classData = teacherClasses.find(c => c.id === classId)
      return classData?.className || 'Bilinmeyen Sınıf'
    })
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Döküman Gönderme
            </Typography>
          </Toolbar>
        </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        ) : (
          <>
            {/* Document Selection Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Döküman Seçimi
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  PDF, Word, Excel, PowerPoint, resim veya metin dosyası seçin
                </Typography>

                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<UploadIcon />}
                  onClick={() => document.getElementById('file-input').click()}
                  sx={{ mb: 2 }}
                >
                  Döküman Seç
                </Button>

                {selectedFile && (
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {selectedFile.name}
                    </Typography>
                    <IconButton size="small" onClick={clearSelectedFile} color="error">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Class Selection Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <GroupIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6" fontWeight="bold" color="secondary">
                    Sınıf Seçimi
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Dökümanı göndermek istediğiniz sınıfları seçin
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<GroupIcon />}
                  onClick={() => {
                    if (teacherClasses.length === 0) {
                      toast.error('Henüz sınıfınız bulunmuyor!')
                      return
                    }
                    setShowClassDialog(true)
                  }}
                  sx={{ mb: 2 }}
                >
                  Sınıf Seç
                </Button>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {teacherClasses.map((classData) => {
                    const studentCount = students.filter(s => s.classId === classData.id).length
                    
                    return (
                      <Chip
                        key={classData.id}
                        label={`${classData.className} (${studentCount} öğrenci)`}
                        onClick={() => handleClassClick(classData)}
                        color="default"
                        variant="outlined"
                        sx={{ mb: 1, cursor: 'pointer' }}
                      />
                    )
                  })}
                </Box>

                                 {/* Seçilen öğrenciler gösterimi */}
                 {selectedStudents.length > 0 && (
                   <Box mt={2}>
                     <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                       <Typography variant="subtitle2" color="primary">
                         Seçilen Öğrenciler ({selectedStudents.length} kişi):
                       </Typography>
                       <IconButton
                         size="small"
                         color="error"
                         onClick={clearSelectedStudents}
                         title="Seçimi Temizle"
                       >
                         <ClearIcon />
                       </IconButton>
                     </Box>
                    
                    {/* Sınıf bazında gruplandırma */}
                    {getSelectedClasses().map((className, index) => {
                      const classStudents = selectedStudents.filter(s => {
                        const classData = teacherClasses.find(c => c.id === s.classId)
                        return classData?.className === className
                      })
                      
                      return (
                        <Box key={index} mb={1}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            {className}:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {classStudents.map((student) => (
                              <Chip
                                key={student.id}
                                label={student.username || student.email}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Send Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
              onClick={handleSendDocument}
              disabled={!selectedFile || selectedStudents.length === 0 || uploading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
              }}
            >
              {uploading ? 'Gönderiliyor...' : `Dökümanı Gönder (${selectedStudents.length} öğrenci)`}
            </Button>

            {/* View Sent Documents Button */}
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={() => toast.success('Gönderilen dökümanlar yükleniyor')}
              sx={{
                mt: 1.5,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.50'
                }
              }}
            >
              Gönderilen Dökümanları Görüntüle
            </Button>

            {/* Info Alert */}
            <Alert severity="info" sx={{ mt: 2 }}>
              Seçilen öğrencilere döküman gönderilecektir.
            </Alert>
          </>
        )}
      </Container>

      {/* Class List Dialog */}
      <Dialog open={showClassDialog && !selectedClass} onClose={() => setShowClassDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Sınıf Seç
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : teacherClasses.length === 0 ? (
            <Alert severity="info">
              Henüz sınıfınız bulunmuyor. Önce sınıf oluşturun.
            </Alert>
          ) : (
            <List>
              {teacherClasses.map((classData) => {
                const studentCount = students.filter(s => s.classId === classData.id).length
                return (
                  <ListItem
                    key={classData.id}
                    button
                    onClick={() => {
                      setSelectedClass(classData)
                      setShowClassDialog(false)
                      // Hemen öğrenci seçimi dialog'unu aç
                      setTimeout(() => {
                        setShowClassDialog(true)
                      }, 100)
                    }}
                  >
                    <ListItemText
                      primary={classData.className}
                      secondary={`${studentCount} öğrenci`}
                    />
                  </ListItem>
                )
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowClassDialog(false)
            setSelectedClass(null)
          }}>İptal</Button>
        </DialogActions>
      </Dialog>

      {/* Class Selection Dialog */}
      <Dialog open={showClassDialog && selectedClass} onClose={() => setShowClassDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Sınıf: {selectedClass?.className}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Bu sınıf için öğrenci seçim yöntemini belirleyin:
          </Typography>
          
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Tüm Öğrencileri Seç */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'primary.50' },
                border: '2px solid',
                borderColor: 'primary.main'
              }}
              onClick={handleSelectAllStudents}
            >
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Tüm Öğrencileri Seç
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Bu sınıftaki tüm öğrencilere döküman gönder
                </Typography>
              </CardContent>
            </Card>

            {/* Bireysel Seçim */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'secondary.50' },
                border: '2px solid',
                borderColor: 'secondary.main'
              }}
              onClick={handleIndividualSelection}
            >
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6" color="secondary" fontWeight="bold">
                    Bireysel Seçim Yap
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Öğrencileri tek tek seç
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowClassDialog(false)
            setSelectedClass(null)
          }}>
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Selection Dialog */}
      <Dialog open={showStudentDialog} onClose={() => setShowStudentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="h6">
              Öğrenci Seçimi - {selectedClass?.className}
            </Typography>
          </Box>
        </DialogTitle>
                 <DialogContent>
           <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
             <Typography variant="body2" color="text.secondary">
               Döküman göndermek istediğiniz öğrencileri seçin:
             </Typography>
             {selectedStudents.length > 0 && (
               <IconButton
                 size="small"
                 color="error"
                 onClick={clearSelectedStudents}
                 title="Seçimi Temizle"
               >
                 <ClearIcon />
               </IconButton>
             )}
           </Box>
          
          <List>
            {classStudents.map((student) => {
              const isSelected = selectedStudents.some(s => s.id === student.id)
              return (
                <ListItem key={student.id} disablePadding>
                  <ListItemButton onClick={() => handleStudentToggle(student)}>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText 
                      primary={student.username || student.email}
                      secondary={student.email}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStudentDialog(false)}>
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default SendDocument
